/**
 * useAnalysisStatus Hook
 *
 * 每 3 秒 polling 分析進度，分析完成或失敗後停止。
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { novelAnalysisApi, type NovelAnalysisStatus } from '../services/api/novel-analysis';

const POLL_INTERVAL = 3000;

export function useAnalysisStatus(novelId: string | undefined, onUpdate?: () => void) {
  const [status, setStatus] = useState<NovelAnalysisStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const onUpdateRef = useRef(onUpdate);
  onUpdateRef.current = onUpdate;

  const fetchStatus = useCallback(async () => {
    if (!novelId) return;
    try {
      const data = await novelAnalysisApi.getStatus(novelId);
      setStatus(data);
      setError(null);

      // Stop polling when done
      if (data.status === 'completed' || data.status === 'failed' || data.status === 'not_started') {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      }

      // Notify caller after each poll
      onUpdateRef.current?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch status');
    } finally {
      setLoading(false);
    }
  }, [novelId]);

  const startPolling = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(fetchStatus, POLL_INTERVAL);
  }, [fetchStatus]);

  // Initial fetch + start polling if in progress
  useEffect(() => {
    if (!novelId) return;

    setLoading(true);
    fetchStatus().then(() => {
      // Start polling if status is in_progress
      if (status?.status === 'in_progress') {
        startPolling();
      }
    });

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [novelId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Start polling when status changes to in_progress
  useEffect(() => {
    if (status?.status === 'in_progress' && !intervalRef.current) {
      startPolling();
    }
  }, [status?.status, startPolling]);

  const refresh = useCallback(async () => {
    setLoading(true);
    await fetchStatus();
  }, [fetchStatus]);

  return { status, loading, error, refresh, startPolling };
}
